/**
 * Entry application component used to compose providers and render Routes.
 * */

import React, { useState, useEffect, useCallback } from "react";
import ReactGA from "react-ga";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { createBrowserHistory } from "history";
import { PersistGate } from "redux-persist/integration/react";
import { LastLocationProvider } from "react-router-last-location";
import { Routes } from "./app/router/Routes";
import { getUserByToken } from "./app/crud/auth.crud";
import { I18nProvider, LayoutSplashScreen } from "./_metronic";
import bsCustomFileInput from "bs-custom-file-input";
import { globalColors } from "./_metronic/_assets/sass/custom/globalColors";
import XavierThemeProvider from "./app/design-system/theme/XavierThemeProvider";

const legacyTheme = {
  colors: {
    primaryColor1: globalColors.primaryColor1,
    primaryColor2: globalColors.primaryColor2,
    primaryColor3: globalColors.primaryColor3,
    primaryColor4: "#C34C3D",
    secondaryColor2: globalColors.secondaryColor2,
    alertColor: "#CA1818",
    textColor1: globalColors.textColor1,
    textColor2: globalColors.textColor2,
    textColor3: "#A1A1AA",
    colorWhite: globalColors.colorWhite,
    backgroundColor1: globalColors.backgroundColor1,
    backgroundColor2: globalColors.primaryColor1,
  },
};

bsCustomFileInput.init();

const history = createBrowserHistory();
const analyticsTrackingId = process.env.REACT_APP_GA_TRACKING_ID;

if (analyticsTrackingId) {
  ReactGA.initialize(analyticsTrackingId);
}

function buildPath(location) {
  return `${location.pathname}${location.hash}`;
}

history.listen((location) => {
  if (!analyticsTrackingId) return;
  const path = buildPath(location);
  ReactGA.set({ page: path }); // Update the user's current page
  ReactGA.pageview(path); // Record a pageview for the given page
});

export default function App({ store, persistor, basename }) {
  const [isLoading, setIsLoading] = useState(true);

  const refreshToken = useCallback(async (token) => {
    if (token) {
      try {
        await getUserByToken();
      } catch (error) {
        console.warn('refreshToken failed:', error);
      }
    }
    setIsLoading(false);
  }, []);

  let auth;
  let token;
  try {
    const persistAuth = window.localStorage.getItem("persist:auth");
    if (persistAuth) {
      const parsed = JSON.parse(persistAuth);
      auth = parsed?.authData;
      if (auth) {
        const parsedAuth = JSON.parse(auth);
        token = parsedAuth?.accessToken;
      }
    }
  } catch (error) {
    console.warn('Invalid persist:auth format:', error);
  }

  useEffect(() => {
    if (analyticsTrackingId) {
      const path = buildPath(history.location);
      ReactGA.set({ page: path });
      ReactGA.pageview(path);
    }
    refreshToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    /* Provide Redux store */
    <XavierThemeProvider legacyTheme={legacyTheme}>
      <Provider store={store} loading={<LayoutSplashScreen />}>
        <PersistGate persistor={persistor}>
          <Router basename={basename} history={history}>
            <LastLocationProvider>
              <I18nProvider>{!isLoading && <Routes />}</I18nProvider>
            </LastLocationProvider>
          </Router>
        </PersistGate>
      </Provider>
    </XavierThemeProvider>
  );
}
